import { Data } from '@generated/Foundation.GMI.model';
import { useSitecore } from '@sitecore-content-sdk/nextjs';
import { DisableFeatureFlag, DisableFeatureFlags } from 'lib/constants/disable-feature-flags';
import { getEnumValues } from 'lib/utils/sitecore/enum';
import { useMemo } from 'react';

type DisabledFlagSource = 'UserRequest' | 'Settings' | 'LanguageSpecificSettings';

export type DisabledFlagValue = {
  disabled: true;
  sources: DisabledFlagSource[];
};

export type EffectiveDisabledFeatures = {
  [key in DisableFeatureFlag]: DisabledFlagValue | false;
};

/**
 * Returns the disabled feature flags.  Features can be disabled in the following ways:
 * - Request: Either the 'disableFeature' query string or cookie is set.  (Comma delimited list of feature flags)
 * - Settings: The feature is disabled at the brand level.
 * - LanguageSpecificSettings: The feature is disabled at the brand and language level.
 *
 * @returns The effective disabled features, either `false` if the feature is not disabled,
 * or an object with the `disabled` property set to `true` and the `source` property
 * set to the source of the disabled feature.
 */
export function useDisabledFeatureFlags(): EffectiveDisabledFeatures {
  const { page } = useSitecore();
  const sitecoreContext = page?.layout?.sitecore?.context as Record<string, unknown> | undefined;
  return useMemo(() => {
    const requestDisableFeatures = (sitecoreContext?.disableFeature as string[] | undefined) ?? [];

    const disableFeatureSettings = (
      sitecoreContext?.siteSettings as
        | { disableFeaturesSettings?: Data.Settings.DisableFeatureSettings }
        | undefined
    )?.disableFeaturesSettings;

    const disableFeatureShared = getEnumValues<DisableFeatureFlag>(
      disableFeatureSettings?.fields?.disableFeaturesShared
    );

    const disableFeaturesLanguageSpecific = getEnumValues<DisableFeatureFlag>(
      disableFeatureSettings?.fields?.disableFeaturesLanguageSpecific
    );

    const effectiveDisabledFeatures: EffectiveDisabledFeatures = {
      DCI: getDisabledFlagValue(DisableFeatureFlags.DCI),
      GTM: getDisabledFlagValue(DisableFeatureFlags.GTM, true),
      MEP: getDisabledFlagValue(DisableFeatureFlags.MEP, true),
      INTERSTITIAL: getDisabledFlagValue(DisableFeatureFlags.INTERSTITIAL, true),
      CAPTCHA: getDisabledFlagValue(DisableFeatureFlags.CAPTCHA),
      TPS: getDisabledFlagValue(DisableFeatureFlags.TPS),
      OT: getDisabledFlagValue(DisableFeatureFlags.OT),
      BV: getDisabledFlagValue(DisableFeatureFlags.BV),
      PEAR: getDisabledFlagValue(DisableFeatureFlags.PEAR),
      ADCHOICES: getDisabledFlagValue(DisableFeatureFlags.ADCHOICES),
    };

    return effectiveDisabledFeatures;
    function getDisabledFlagValue(
      feature: DisableFeatureFlag,
      allowUserDisableInProd: boolean = false
    ): DisabledFlagValue | false {
      const sources: DisabledFlagSource[] = [];

      if (requestDisableFeatures.includes(feature)) {
        // Only add if the user is not in production or we allowed the feature to be disabled in production
        if (process.env.GMIServer !== 'Production' || allowUserDisableInProd) {
          sources.push('UserRequest');
        }
      }
      if (disableFeatureShared?.includes(feature)) {
        sources.push('Settings');
      }
      if (disableFeaturesLanguageSpecific?.includes(feature)) {
        sources.push('LanguageSpecificSettings');
      }
      return sources.length > 0 ? { disabled: true, sources } : false;
    }
  }, [sitecoreContext]);
}
